import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React from 'react';
import {
  WorkflowDto,
  WorkflowRoleDto,
  WorkflowStepDto,
  WorkflowUserDto,
} from 'src/api/FiretrolApiClient';
import { Modal } from 'src/design/molecules/Modal';
import { BasicButton } from 'src/design/atoms/BasicButton';

export interface IWorkflowPreviewProps {
  isOpen: boolean;
  steps: WorkflowStepDto[];
  settings: WorkflowDto;
  setOpen: (isOpen: boolean) => void;
}

/** @registry Modal previewing a workflow's initiation settings and step-by-step configuration before saving. */
export const WorkflowPreview: React.FC<IWorkflowPreviewProps> = ({
  isOpen,
  settings,
  steps,
  setOpen,
}) => {
  const renderBadge = (text: string | React.ReactNode) => {
    return <Badge variant="light">{text}</Badge>;
  };

  const renderTextGroup = (text: string, sibling: React.ReactNode) => {
    return (
      <Group>
        <Text>{text}</Text>
        {sibling}
      </Group>
    );
  };

  const renderTextWithButton = (text: string, button: string) => {
    return (
      <Group>
        <Text>{text}</Text>
        <BasicButton size="xs" variant="filled">
          {button}
        </BasicButton>
      </Group>
    );
  };

  const renderTeamUserNames = (users: WorkflowUserDto[]) => {
    if (users.length === 0) {
      return renderBadge('None');
    }
    return users.map((user) => (
      <Badge key={user.id} variant="light">
        <Text span td="line-through" size="xs">
          {user.jobUser?.user.fullName}
        </Text>
      </Badge>
    ));
  };

  const renderRoles = (roles: WorkflowRoleDto[]) => {
    if (roles.length === 0) {
      return renderBadge('None');
    }
    return roles.map((role) => (
      <Badge key={role.id} variant="light">
        {role.organizationRole?.name}
      </Badge>
    ));
  };

  const renderSettings = () => {
    return renderTextGroup(
      'Allow Drafts:',
      renderBadge(settings.allowDraft ? 'TRUE' : 'FALSE'),
    );
  };

  const renderFirstStep = () => {
    return (
      <>
        <Title order={4}>Initiate Workflow</Title>
        {renderTextWithButton('Button Text:', settings.createButtonText)}
        {renderTextGroup(
          'Allowed Roles:',
          renderRoles(settings.authorizedRoles),
        )}
        {renderTextGroup(
          'Allowed Users:',
          renderTeamUserNames(settings.authorizedUsers),
        )}
        {renderTextGroup(
          'Excluded Users:',
          renderTeamUserNames(settings.excludedUsers),
        )}
      </>
    );
  };

  const renderSteps = () => {
    return steps.map((step, idx) => {
      return (
        <React.Fragment key={`${step.id}-${idx}`}>
          <Title order={4}>
            {idx + 1}. {step.label}
          </Title>
          {renderTextWithButton('Button Text:', step.buttonText)}
          {step.isLastStep && (
            <Text fw={500} c="blue">
              Items with this status are considered complete
            </Text>
          )}
          {!step.isLastStep && (
            <>
              {step.isExternalStep && (
                <>
                  <Text fw={500}>Assigned to External Customer</Text>
                  {renderTextGroup(
                    'Can reject from this status:',
                    renderBadge(step.allowReject ? 'TRUE' : 'FALSE'),
                  )}
                  {renderTextGroup(
                    'Can revise to draft:',
                    renderBadge(step.allowRevise ? 'TRUE' : 'FALSE'),
                  )}
                </>
              )}
              {!step.isExternalStep && (
                <>
                  {renderTextGroup(
                    'Assigned to Roles:',
                    renderRoles(step.assignedRoles),
                  )}
                  {renderTextGroup(
                    'Assigned to Users:',
                    renderTeamUserNames(step.assignedUsers),
                  )}
                  {renderTextGroup(
                    'Excluded Assigned Users:',
                    renderTeamUserNames(step.excludedAssignedUsers),
                  )}
                  {renderTextGroup(
                    'Can reject from this status:',
                    renderBadge(step.allowReject ? 'TRUE' : 'FALSE'),
                  )}
                  {renderTextGroup(
                    'Can revise to draft:',
                    renderBadge(step.allowRevise ? 'TRUE' : 'FALSE'),
                  )}
                  {renderTextGroup(
                    'Can void from this status:',
                    renderBadge(step.allowVoid ? 'TRUE' : 'FALSE'),
                  )}
                </>
              )}
            </>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Modal
      opened={isOpen}
      onClose={() => setOpen(false)}
      title="Preview Workflow"
      size="lg"
    >
      <Stack gap="lg">
        {renderSettings()}
        {renderFirstStep()}
        {renderSteps()}
      </Stack>
    </Modal>
  );
};
